// PartnersEOI — Aus/NZ distributor & partner Expression of Interest page.
// Linked from the footer and shared directly with prospective distributors.
import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Storefront, HandHeart, Globe } from "@phosphor-icons/react";
import { SiteNav } from "../components/SiteNav";
import { Footer } from "../components/sections/Footer";
import { ForgetMeNotIcon } from "../components/ForgetMeNotIcon";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const FADE = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } };

const ROLES = [
    ["distributor", "Distributor"],
    ["retailer", "Retailer"],
    ["care_provider", "Aged-care / retirement living provider"],
    ["health_professional", "Health professional"],
    ["charity", "Charity / community organisation"],
    ["investor", "Investor"],
    ["other", "Other"],
];

const VOLUMES = ["Pilot (under 100 units)", "100–1,000 units / yr", "1,000–10,000 units / yr", "10,000+ units / yr", "Not sure yet"];

const PILLARS = [
    { icon: Storefront, title: "A range, not a product", body: "Lavender dementia editions (15″ & 17″), kids pink & blue, custom covers and a BYO software tier — one platform, a shelf of giftable editions." },
    { icon: HandHeart, title: "A story that sells itself", body: "Built by Lea, a hairdresser of 53 years inside a retirement village, on 30 years of reminiscence-therapy research. Every edition gives a portion to a matched charity." },
    { icon: Globe, title: "Ready for this market", body: "12 languages, one-touch tablets designed for aged care, drop-shipped fulfilment, and demos (John & Ella) your buyers can experience in two minutes." },
];

export default function PartnersEOI() {
    const [form, setForm] = useState({ name: "", organisation: "", email: "", phone: "", role: "distributor", country: "AU", volume_interest: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    async function submit(e) {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/partners/eoi", { ...form, phone: form.phone || null, volume_interest: form.volume_interest || null, message: form.message || null });
            setDone(true);
            toast.success("Thank you — we'll be in touch within two business days.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send your expression of interest. Please try again.");
        } finally {
            setSubmitting(false);
        }
    }

    const inputCls = "w-full px-5 py-3 rounded-full text-sm outline-none transition-all bg-white text-[#2D2B2A] placeholder-[#8A8786] border border-[#E5E1D8] focus:border-[#8C9ECB]";

    return (
        <div className="min-h-screen bg-[#FAF7F2]">
            <SiteNav />
            <section className="pt-64 pb-20 px-6" data-testid="partners-hero">
                <div className="max-w-6xl mx-auto">
                    <motion.div {...FADE} className="max-w-3xl">
                        <div className="flex items-center gap-2 mb-5">
                            <ForgetMeNotIcon size={22} />
                            <p className="font-sans text-[11px] uppercase tracking-[0.24em] font-semibold text-[#6B7DB8]">
                                Partners & distributors — Australia · New Zealand
                            </p>
                        </div>
                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2D2B2A] leading-[1.08]">
                            Bring remembr to the families <em className="italic text-[#4A5A95]">who need it most.</em>
                        </h1>
                        <p className="mt-6 text-base md:text-lg text-[#5C5A59] leading-relaxed max-w-2xl">
                            We're opening distribution for Australia and New Zealand — aged-care groups, retirement villages,
                            pharmacy and gift retail, and health channels. Register your interest below and we'll send the
                            Information Memorandum and wholesale terms.
                        </p>
                    </motion.div>

                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
                        {PILLARS.map((p, i) => (
                            <motion.div key={p.title} {...FADE} transition={{ ...FADE.transition, delay: 0.1 * i }}
                                className="rounded-[1.75rem] bg-white border border-[#E5E1D8]/70 p-7 shadow-rem-soft">
                                <p.icon size={26} className="text-[#6B7DB8] mb-4" />
                                <h3 className="font-serif text-xl text-[#2D2B2A] mb-2">{p.title}</h3>
                                <p className="text-sm text-[#5C5A59] leading-relaxed">{p.body}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="pb-28 px-6" data-testid="partners-eoi-section">
                <div className="max-w-3xl mx-auto">
                    <motion.div {...FADE} className="rounded-[2rem] bg-white border border-[#E5E1D8]/80 p-8 sm:p-12 shadow-rem-card">
                        <h2 className="font-serif text-3xl text-[#2D2B2A] mb-2">Expression of interest</h2>
                        <p className="text-sm text-[#8A8786] mb-8">No commitment — this simply opens the conversation.</p>
                        {done ? (
                            <div className="rounded-2xl p-6 bg-[#F4F6FB] text-[#4A5A95] text-sm font-medium" data-testid="eoi-done">
                                🌸 Received — thank you. We'll reply within two business days with the IM pack and next steps.
                            </div>
                        ) : (
                            <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="eoi-form">
                                <input required placeholder="Your name" value={form.name} onChange={set("name")} className={inputCls} data-testid="eoi-name" />
                                <input required placeholder="Organisation" value={form.organisation} onChange={set("organisation")} className={inputCls} data-testid="eoi-organisation" />
                                <input required type="email" placeholder="Work email" value={form.email} onChange={set("email")} className={inputCls} data-testid="eoi-email" />
                                <input placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="eoi-phone" />
                                <select value={form.role} onChange={set("role")} className={inputCls} data-testid="eoi-role">
                                    {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </select>
                                <select value={form.country} onChange={set("country")} className={inputCls} data-testid="eoi-country">
                                    <option value="AU">Australia</option>
                                    <option value="NZ">New Zealand</option>
                                    <option value="other">Other</option>
                                </select>
                                <select value={form.volume_interest} onChange={set("volume_interest")} className={`${inputCls} sm:col-span-2`} data-testid="eoi-volume">
                                    <option value="">Indicative volume (optional)</option>
                                    {VOLUMES.map((v) => <option key={v} value={v}>{v}</option>)}
                                </select>
                                <textarea rows={4} placeholder="Tell us about your channel, region, or anything else (optional)"
                                    value={form.message} onChange={set("message")}
                                    className="sm:col-span-2 w-full px-5 py-4 rounded-3xl text-sm outline-none transition-all bg-white text-[#2D2B2A] placeholder-[#8A8786] border border-[#E5E1D8] focus:border-[#8C9ECB] resize-none"
                                    data-testid="eoi-message" />
                                <button type="submit" disabled={submitting}
                                    className="sm:col-span-2 inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full font-medium text-sm transition-all hover:-translate-y-0.5 disabled:opacity-60 bg-[#6B7DB8] hover:bg-[#4A5A95] text-white"
                                    data-testid="eoi-submit">
                                    {submitting ? "Sending…" : "Register interest"}
                                    <ArrowRight weight="bold" size={16} />
                                </button>
                            </form>
                        )}
                        <p className="mt-6 text-xs italic text-[#8A8786]">
                            * Pricing shown on remembr.it.com is indicative until final tablet costs are locked in. Wholesale terms are shared under the IM.
                        </p>
                    </motion.div>
                </div>
            </section>
            <Footer />
        </div>
    );
}
